// kommunikationAnrufe.ts — reine Logik der Anruf-Tabelle im Kontakt-Reiter
// „Kommunikation": Anzeige-Texte und Spaltenfilter. Frei von React/DOM, damit
// sie per Vitest geprüft werden kann (die View zieht Compose-Fenster & Co. mit).

import type { CallDirection, CallLogDto } from "../services/telephonyService";
import { zeitImDatumsbereich, type DateRangeValue } from "./redesign/table/filterModel";
import { passtZurSuche, suchWoerter } from "./kommunikationTabelle";

/** „Nicht erreicht": eingehend/ausgehend ohne nennenswerte Gesprächsdauer. */
export function isMissed(call: CallLogDto): boolean {
    const talk = call.talkDurationSec ?? 0;
    if (talk > 0) return false;
    const status = (call.status ?? "").toUpperCase();
    if (status.includes("ANSWER") && !status.includes("NO")) return false;
    return true;
}

export function directionLabel(direction: CallDirection): string {
    switch (direction) {
        case "inbound":
            return "Eingehend";
        case "outbound":
            return "Ausgehend";
        case "internal":
            return "Intern";
        default:
            return direction;
    }
}

export const startZeit = (call: CallLogDto): number => {
    const t = new Date(call.startedAt).getTime();
    return Number.isFinite(t) ? t : 0;
};

// ── Spaltenfilter der Anruf-Tabelle (Lupen im Kopf) ─────────────────────────

export const ANRUF_RICHTUNGEN = ["Eingehend", "Ausgehend", "Intern"] as const;
export const ANRUF_STATUS_OFFEN = "Nicht erreicht (offen)";
export const ANRUF_STATUS_ERLEDIGT = "Erledigt";
export const ANRUF_STATUS_GESPROCHEN = "Gesprochen";
export const ANRUF_STATUS = [ANRUF_STATUS_OFFEN, ANRUF_STATUS_ERLEDIGT, ANRUF_STATUS_GESPROCHEN] as const;

export function anrufStatusLabel(call: CallLogDto): string {
    if (call.resolvedAt) return ANRUF_STATUS_ERLEDIGT;
    return isMissed(call) ? ANRUF_STATUS_OFFEN : ANRUF_STATUS_GESPROCHEN;
}

/** Wendet die Spaltenfilter auf die GELADENEN Anrufe an (alle aktiven müssen passen). */
export function filtereAnrufSpalten(
    calls: CallLogDto[],
    werte: Record<string, string | boolean | undefined>,
    datum?: DateRangeValue,
): CallLogDto[] {
    const w = (id: string) => {
        const v = werte?.[id];
        return typeof v === "string" && v.trim() ? v : undefined;
    };
    const richtung = w("richtung");
    const gegenstelle = w("gegenstelle");
    const status = w("status");
    return calls.filter((c) => {
        if (!zeitImDatumsbereich(startZeit(c), datum)) return false;
        if (richtung && directionLabel(c.direction) !== richtung) return false;
        if (status && anrufStatusLabel(c) !== status) return false;
        if (gegenstelle) {
            const text = [c.contactDisplayName, c.fromNumber, c.toNumber].filter(Boolean).join(" ").toLowerCase();
            if (!passtZurSuche(text, suchWoerter(gegenstelle))) return false;
        }
        return true;
    });
}

